import React, { useState, useRef, useEffect } from 'react';
import useOnClickOutside from 'use-onclickoutside';
import { usePopupPosition } from '../../hooks';
import clsx from 'clsx';
import HelpIcon from '@mui/icons-material/Help';

export type TooltipProps = {
  content: React.ReactNode;
  className?: string;
  iconClassName?: string;
  popupClassName?: string;
  position?: 'left' | 'top' | 'right' | 'bottom';
  customIcon?: React.ReactNode;
  children?: React.ReactNode;
  trigger?: 'click' | 'hover' | 'both';
};

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  className,
  iconClassName,
  popupClassName,
  position = 'top',
  customIcon,
  children,
  trigger = 'click',
}) => {
  const [open, setOpen] = useState(false);
  const [customPosition, setCustomPosition] = useState<{
    left: number;
    top: number;
    tailOffset?: number;
    actualTailPosition?: 'left' | 'top' | 'right' | 'bottom';
  } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const {
    popupRef: positionPopupRef,
    anchorRef: popupAnchorRef,
    popupPosition,
    anchorPosition,
  } = usePopupPosition();

  const supportsClick = trigger === 'click' || trigger === 'both';
  const supportsHover = trigger === 'hover' || trigger === 'both';

  useOnClickOutside(anchorRef as any, () => {
    if (supportsClick) {
      setOpen(false);
    }
  });

  const handleMouseEnter = () => {
    if (supportsHover) {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (supportsHover) {
      setOpen(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (supportsClick) {
      e.stopPropagation();
      setOpen(prev => !prev);
    }
  };

  useEffect(() => {
    if (!open || !anchorRef.current || !popupRef.current) {
      setCustomPosition(null);
      return;
    }

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = 0;
    let top = 0;
    let tailOffset = 0;
    const offset = 8;

    switch (position) {
      case 'top':
        left = anchorRect.left + anchorRect.width / 2 - popupRect.width / 2;
        top = anchorRect.top - popupRect.height - offset;
        break;
      case 'bottom':
        left = anchorRect.left + anchorRect.width / 2 - popupRect.width / 2;
        top = anchorRect.bottom + offset;
        break;
      case 'left':
        left = anchorRect.left - popupRect.width - offset;
        top = anchorRect.top + anchorRect.height / 2 - popupRect.height / 2;
        break;
      case 'right':
        left = anchorRect.right + offset;
        top = anchorRect.top + anchorRect.height / 2 - popupRect.height / 2;
        break;
    }

    if (left < 0) left = 8;
    if (left + popupRect.width > viewportWidth)
      left = viewportWidth - popupRect.width - 8;
    if (top < 0) top = 8;
    if (top + popupRect.height > viewportHeight)
      top = viewportHeight - popupRect.height - 8;

    if (position === 'top' || position === 'bottom') {
      const anchorCenterX = anchorRect.left + anchorRect.width / 2;
      const tooltipCenterX = left + popupRect.width / 2;
      tailOffset = anchorCenterX - tooltipCenterX;

      const maxOffset = popupRect.width / 2 - 16;
      tailOffset = Math.max(-maxOffset, Math.min(maxOffset, tailOffset));
    }

    if (position === 'left' || position === 'right') {
      const anchorCenterY = anchorRect.top + anchorRect.height / 2;
      const tooltipCenterY = top + popupRect.height / 2;
      tailOffset = anchorCenterY - tooltipCenterY;

      const maxOffset = popupRect.height / 2 - 16;
      tailOffset = Math.max(-maxOffset, Math.min(maxOffset, tailOffset));
    }

    setCustomPosition({ left, top, tailOffset });
  }, [open, position, anchorPosition, popupPosition]);

  const getTailStyles = () => {
    if (!position) return {};

    const baseTailStyle = {
      position: 'absolute' as const,
      width: 0,
      height: 0,
      borderStyle: 'solid' as const,
    };

    const tailOffset = customPosition?.tailOffset || 0;

    switch (position) {
      case 'bottom':
        return {
          ...baseTailStyle,
          top: '-7.5px',
          left: `calc(50% + ${tailOffset}px)`,
          transform: 'translateX(-50%)',
          borderWidth: '0 8px 8px 8px',
          borderColor: 'transparent transparent rgba(0, 0, 0, 1) transparent',
        };
      case 'left':
        return {
          ...baseTailStyle,
          right: '-7.5px',
          top: `calc(50% + ${tailOffset}px)`,
          transform: 'translateY(-50%)',
          borderWidth: '8px 0 8px 8px',
          borderColor: 'transparent transparent transparent rgba(0, 0, 0, 1)',
        };
      case 'right':
        return {
          ...baseTailStyle,
          left: '-7.5px',
          top: `calc(50% + ${tailOffset}px)`,
          transform: 'translateY(-50%)',
          borderWidth: '8px 8px 8px 0',
          borderColor: 'transparent rgba(0, 0, 0, 1) transparent transparent',
        };
      case 'top':
        return {
          ...baseTailStyle,
          bottom: '-7.5px',
          left: `calc(50% + ${tailOffset}px)`,
          transform: 'translateX(-50%)',
          borderWidth: '8px 8px 0 8px',
          borderColor: 'rgba(0, 0, 0, 1) transparent transparent transparent',
        };
      default:
        return {};
    }
  };

  return (
    <div
      ref={node => {
        node && popupAnchorRef(node);
        anchorRef.current = node;
      }}
      className={clsx('relative', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children ? (
        <div onClick={handleClick} className="cursor-pointer">
          {children}
        </div>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          className={clsx(
            'cursor-pointer text-gray-500 hover:text-gray-700 flex items-center justify-center shrink-0 grow-0',
            iconClassName
          )}
        >
          {customIcon || <HelpIcon fontSize="medium" />}
        </button>
      )}

      <div
        ref={node => {
          if (node) {
            popupRef.current = node;
            positionPopupRef(node);
          }
        }}
        style={{
          ...(customPosition
            ? {
                left: customPosition.left,
                top: customPosition.top,
                minWidth: anchorPosition.width ?? '10rem',
              }
            : {
                left: popupPosition?.style?.left ?? 0,
                top: popupPosition?.style?.top ?? 0,
                minWidth: anchorPosition.width ?? '10rem',
              }),
          zIndex: 9999,
        }}
        className={clsx(
          'fixed transition-opacity duration-200',
          open ? 'visible opacity-100' : 'invisible opacity-0'
        )}
      >
        <div
          className={clsx(
            'bg-black opacity-70  text-white rounded-md  p-3 text-tiny max-w-md relative',
            popupClassName
          )}
        >
          {content}
          {position && <div style={getTailStyles()} />}
        </div>
      </div>
    </div>
  );
};
